import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SITE_URL = 'https://rewordgenie.com';
const SITE_NAME = 'RewordGenie';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

const OG_LOCALES = {
  en: 'en_US',
  sl: 'sl_SI',
};

/**
 * Reusable SEO head component built on react-helmet-async.
 * Handles title, description, canonical, Open Graph, Twitter, hreflang,
 * and optional JSON-LD structured data for a page.
 *
 * Note: this app is client-rendered (CRA, no SSR), so these tags are
 * injected after JS executes. Static fallbacks live in public/index.html.
 */
export default function SEO({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd,
}) {
  const { i18n } = useTranslation();
  const lang = OG_LOCALES[i18n.language] ? i18n.language : 'en';
  const canonical = `${SITE_URL}${path}`;
  const absoluteImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonical} />

      {/* hreflang alternates — language is selected in-app and persisted via ?lang= */}
      <link rel="alternate" hrefLang="en" href={`${SITE_URL}${path}`} />
      <link rel="alternate" hrefLang="sl" href={`${SITE_URL}${path}?lang=sl`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${path}`} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={OG_LOCALES[lang]} />
      {Object.entries(OG_LOCALES)
        .filter(([code]) => code !== lang)
        .map(([, ogLocale]) => (
          <meta key={ogLocale} property="og:locale:alternate" content={ogLocale} />
        ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />

      {/* Structured data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
